import type { HkTableState } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { fa } from "./cards";
import { Overlay } from "../ui/Overlay";

/**
 * جدولِ نتایجِ هفت خبیث — همتای `ShowRoundOver` اپ.
 *
 * پیش از این فقط یک «بردی/باختی» با چند عدد کنارِ هم بود و بازیکن نمی‌فهمید
 * چه شد: امتیازِ کدام دور بود، رتبه‌اش چند است، چقدر تا لیمیت مانده. حالا
 * همان چیزی می‌آید که در اپ می‌آید — سربرگِ نتیجه، زیرنویسِ رتبه، جدولِ
 * دورها با برنده‌ی هر دور، و ردیفِ «جمع · رتبه».
 *
 * در وب دکمه‌ی «دورِ بعد» معنا ندارد: سرور خودش بعد از مکث دستِ تازه می‌دهد،
 * پس پایانِ دور فقط خبر می‌دهد و خودش می‌رود.
 */
export function HaftResults({ state }: { state: HkTableState }) {
  const seats = state.seats;
  const n = seats.length;
  const scores = state.scores.map(Number);
  const places = placesOf(scores);
  const leader = scores.indexOf(Math.min(...scores));
  // تماشاچی «من» ندارد: صندلیِ ۰ فقط لنگرِ چرخش است، پس نه «تو» می‌گیرد و
  // نه رتبه‌ی شخصی. وگرنه تماشاچی «مسابقه را بردی!» می‌دید.
  const watching = state.watching;
  const me = watching ? -1 : state.me;
  const myPlace = places[me] ?? 1;

  const winner = state.matchOver ? state.matchWinner : state.winner;
  const iTookIt = !watching && winner === me;
  const name = (p: number) => (p === me ? "تو" : seats[p]?.name || "بازیکن");

  const toLimit = state.targetScore > 0 ? Math.max(0, state.targetScore - (scores[me] ?? 0)) : -1;

  return (
    <Overlay label="نتایجِ دور">
      <div className="results">
        <div className={`rtitle ${iTookIt ? "won" : ""}`}>
          {state.matchOver
            ? (iTookIt ? "مسابقه را بردی!" : `${name(winner)} مسابقه را بُرد`)
            : (iTookIt ? "بردی!" : `${name(winner)} بُرد`)}
        </div>

        <div className="rsub">
          {watching
            ? `کم‌ترین امتیاز می‌بَرد · لیمیتِ ${fa(state.targetScore)}`
            : state.matchOver
            ? `لیمیتِ ${fa(state.targetScore)} رد شد · کم‌ترین امتیاز بُرد · رتبه‌ی تو: ${ordinal(myPlace)}`
            : toLimit > 0
              ? `رتبه‌ی تو: ${ordinal(myPlace)} از ${fa(n)} · ${fa(toLimit)} امتیاز تا لیمیتِ ${fa(state.targetScore)}`
              : `رتبه‌ی تو: ${ordinal(myPlace)} از ${fa(n)} · کم‌ترین امتیاز می‌بَرد`}
        </div>

        <div className="rgrid">
          <table>
            <thead>
              <tr>
                <th>دور</th>
                {seats.map((s) => (
                  <th key={s.index} className={s.index === me ? "mine" : ""}>
                    {s.index === me ? "تو" : s.name || "بازیکن"}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {state.rounds.map((r, i) => (
                <tr key={i}>
                  <th>{ordinal(i + 1)}</th>
                  {seats.map((s) => (
                    <td key={s.index} className={s.index === r.winner ? "on" : ""}>
                      {fa(Number(r.points[s.index] ?? 0))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th>جمع · رتبه</th>
                {seats.map((s) => (
                  <td key={s.index} className={s.index === leader ? "on" : ""}>
                    {fa(scores[s.index] ?? 0)} · {ordinal(places[s.index] ?? 1)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>

        {state.matchOver
          ? <button className="btn" style={{ maxWidth: 260 }} onClick={() => client.exitTable()}>
              {watching ? "پایانِ تماشا" : "بازگشت به لابی"}
            </button>
          : <div className="rnext">دستِ بعد به‌زودی…</div>}
      </div>
    </Overlay>
  );
}

/**
 * رتبه با تساویِ مشترک — همان قاعده‌ی `Standings.Places` اپ: هر که جلوتر است
 * یک پله بالاتر، و دو امتیازِ برابر یک رتبه (۱، ۲، ۲، ۴). در هفت خبیث امتیاز
 * جریمه است، پس کم‌ترین، اول.
 */
function placesOf(scores: number[]): number[] {
  return scores.map((s) => 1 + scores.filter((o) => o < s).length);
}

const ordinals = ["اول", "دوم", "سوم", "چهارم", "پنجم", "ششم", "هفتم", "هشتم", "نهم", "دهم", "یازدهم", "دوازدهم", "سیزدهم"];

function ordinal(k: number): string {
  return k >= 1 && k <= ordinals.length ? ordinals[k - 1]! : fa(k);
}
